import { useMemo } from 'react'

import { PatchCollection, Recipe } from '@reduxjs/toolkit/dist/query/core/buildThunks'
import {
  DefinitionsFromApi,
  OverrideResultType,
  TagTypesFromApi,
} from '@reduxjs/toolkit/dist/query/endpointDefinitions'
import { useRouter } from 'next/router'

import { useAppSelector } from '@/client/hooks'
import { ENABLED_OFFER_STATES_GROUP } from '@/constants/groups'
import {
  Application,
  ApplicationSubscriptionDocument,
  GetApplicationQuery,
  api as generatedApi,
} from '@/graphql/generated'
import { useGetLoggedUserQuery } from '@/services/user'
import { GraphQLSubscription } from '@/utils/GraphQLSubscription'
import { isLockOverride, isSuperUser, userHasGroup } from '@/utils/user'

type Definitions = DefinitionsFromApi<typeof generatedApi>
type TagTypes = TagTypesFromApi<typeof generatedApi>

type getApplicationDefinition = OverrideResultType<Definitions['getApplication'], Application>

type UpdatedDefitions = Omit<Definitions, 'getApplication'> & {
  getApplication: getApplicationDefinition
}

const applicationSubscribe = async (
  externalId: string,
  query: string,
  subscriptionName: string,
  updateCachedData: (updateRecipe: Recipe<any>) => PatchCollection
) => {
  try {
    GraphQLSubscription.connect()

    const variables = {
      externalId: externalId,
    }

    const cacheInvalidationHandler = async (message) => {
      if (message?.payload?.data && subscriptionName in message?.payload?.data)
        updateCachedData((draft) => {
          const data = message.payload.data[subscriptionName]
          if (draft && data && data?.externalId === externalId) Object.assign(draft, data)
        })
    }

    GraphQLSubscription.subscribe(query, variables, cacheInvalidationHandler)
  } catch (error) {
    console.error(error)
  }
}

export const applicationApi = generatedApi.enhanceEndpoints<TagTypes, UpdatedDefitions>({
  endpoints: {
    getApplication: {
      providesTags: ['gql:application'],
      async onCacheEntryAdded(arg, { updateCachedData }) {
        applicationSubscribe(
          arg?.externalId,
          ApplicationSubscriptionDocument,
          'applicationSubscription',
          updateCachedData
        )
      },
      transformResponse: (resp: GetApplicationQuery): Application => {
        return resp?.application as Application
      },
    },
    getInvestmentClosingDetailsDates: {
      providesTags: ['gql:investmentClosingDetailsDates'],
    },
  },
})

export const useCurrentApplication = () => {
  const router = useRouter()

  const pipelineSelectedAppId = useAppSelector((state) => state.workQueues.selectedAppId)

  const adminSelectedAppId = useAppSelector((state) => state.fileManager.adminSelectedAppId)

  const id =
    router.route.includes('work-queues') ||
    router.route.includes('work-views-pipeline') ||
    router.route.includes('work-views')
      ? pipelineSelectedAppId
      : router.route.includes('admin')
        ? adminSelectedAppId
        : (router.query.id as string)

  const application = useGetApplicationQuery(
    { externalId: id },
    {
      skip: !id,
    }
  )

  return application
}

export const useIsLockedOffer = () => {
  const { data: application } = useCurrentApplication()
  const { data: loggedUser } = useGetLoggedUserQuery()

  const isLockedOffer = useMemo(() => {
    if (isLockOverride(loggedUser) || isSuperUser(loggedUser)) {
      return false
    }

    for (const group of Object.keys(ENABLED_OFFER_STATES_GROUP)) {
      if (
        userHasGroup(loggedUser, group) &&
        ENABLED_OFFER_STATES_GROUP?.[group]?.includes(application?.applicationState)
      ) {
        return false
      }
    }
    return true
  }, [application, loggedUser])

  return { isLockedOffer }
}

export const {
  useGetApplicationQuery,
  useLazyGetApplicationQuery,
  useGetInvestmentClosingDetailsDatesQuery,
} = applicationApi
